import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import reactHtmlParser from 'react-html-parser';

import { Currency } from '../customFormFields/Currency';
import { Numbers } from '../customFormFields/Numbers';
import { Percent } from '../customFormFields/Percent';
import { DatePickerField } from '../customFormFields/DatePickerField';
import { LongText } from '../customFormFields/LongText';
import { ShortText } from '../customFormFields/ShortText';
import { BooleanField } from '../customFormFields/Boolean';
import { MediumText } from '../customFormFields/MediumText';
import { PicklistField } from '../customFormFields/Picklist';
import { LookupField } from '../customFormFields/Lookup';
import { RequiredTimeField } from './RequiredTimeField';
import moment from 'moment';
import { Timekeepers } from './Timekeepers';


export const TimeConfigFields = ({ configFields, handleChange, updateRequiredFields }) => {
  const timeNamespace = useSelector((state) => state.mail.settings?.override?.timeNamespace || 'litify_time');

  const allSelectedRecords = useSelector((state) => state.mail.selectedRecords);
  const objectsConfiguredForTime = useSelector((state) => state.time?.config?.objectsConfiguredForTime || []);
  const selectedTimeRecord = useSelector((state) => state.time.selectedTimeRecord);
  const selectedRecords = Object.fromEntries(Object.entries(allSelectedRecords).filter(([key, obj]) => objectsConfiguredForTime.includes(obj.type)));
  const [selectedRecord, setSelectedRecord] = useState(allSelectedRecords[selectedTimeRecord?.[`${timeNamespace}__lit_Related_Record_ID__c`]] || selectedRecords[Object.keys(selectedRecords)[0]]);
  const displaySettings = useSelector((state) => state.mail.settings.objectsAndDisplayFields);
  const displayField = displaySettings.find((setting) => setting.apiName === selectedRecord.type).displayFields[0].apiName;
  const timekeeperId = useSelector((state) => state.auth.user.salesforceUserId);
  const billingCodeSets = useSelector((state) => state.time.billingCodeSets);

  useEffect(() => {
    const reqs = {
      [`${timeNamespace}__lit_Related_Record_ID__c`]: selectedRecord.Id,
      OwnerId: timekeeperId,
      [`${timeNamespace}__lit_Date__c`]: moment(new Date()).format('YYYY-MM-DD'),
      [`${timeNamespace}__lit_Duration__c`]: 360,
    };
    for (const field in configFields) {
      if (field.fieldType === 'boolean' && field.default !== null) {
        reqs[field.name] = field.default;
      }
    }
    updateRequiredFields(reqs);
  }, []);

  const requiredFields = ['Primary Related Record', 'Owner ID', 'Rate', 'Duration'];
  const displayFields = configFields.map((field) => {
    if (requiredFields.includes(field.label)) {
      if (field.name === `${timeNamespace}__lit_Rate__c`) {
        return <PicklistField key={`${field.label}-${field.name}`} enabled={false} required={true} label={field.label} fieldName={field.name} handleChange={handleChange} values={[]} />;
      }
      return;
    }


    let taskCodeValues = [];
    let activityCodeValues = [];

    // handle billing codes separately
    if (field.name === `${timeNamespace}__lit_Activity_Code__c`) {
      if (billingCodeSets?.[selectedRecord.Id]?.length) {
        activityCodeValues = field.value.filter((value) => billingCodeSets[selectedRecord.Id].includes(value.SetId));
      } else {
        activityCodeValues = field.value;
      }
      return <LookupField key={field.name} enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} lookupOptions={activityCodeValues} error={field.error || ''} defaultValue={field.default} clearOnOptionsUpdate/>;
    }
    if (field.name === `${timeNamespace}__lit_Task_Code__c`) {
      if (billingCodeSets?.[selectedRecord.Id]?.length) {
        taskCodeValues = field.value.filter((value) => billingCodeSets[selectedRecord.Id].includes(value.SetId));
      } else {
        taskCodeValues = field.value;
      }
      return <LookupField key={field.name} enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} lookupOptions={taskCodeValues} error={field.error || ''} defaultValue={field.default} clearOnOptionsUpdate/>;
    }

    switch (field.fieldType) {
      case 'string':
        return field.length <= 18 ?
        <ShortText enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} length={field.length} error={field.error || ''} defaultValue={field.default} /> :
        <MediumText enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} length={field.length} error={field.error || ''} defaultValue={field.default} />;
      case 'picklist':
        return <PicklistField enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} values={field.values} error={field.error || ''} defaultValue={field.default} />;
      case 'textarea':
        return <LongText enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} length={field.length} error={field.error || ''} defaultValue={field.default}/>;
      case 'date':
        return <DatePickerField enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} error={field.error || ''} defaultValue={field.default}/>;
      case 'double':
        return <Numbers enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} error={field.error || ''} defaultValue={field.default}/>;
      case 'lookup':
        return <LookupField enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} lookupOptions={field.value} error={field.error || ''} defaultValue={field.default}/>;
      case 'currency':
        return <Currency enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} error={field.error || ''} defaultValue={field.default}/>;
      case 'percent':
        return <Percent enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} error={field.error || ''} defaultValue={field.default}/>;
      case 'boolean':
        return <BooleanField enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} defaultValue={field.default} error={field.error || ''} />;
    }
  });
  return (
    <div>
      <RequiredTimeField label={'Primary Related Record'} value={reactHtmlParser(selectedRecord[displayField])} objectType={selectedRecord.type} handleChange={handleChange} selectedRecord={selectedRecord} setSelectedRecord={setSelectedRecord} />
      <Timekeepers label={'Timekeeper'} fieldName={'OwnerId'} handleChange={handleChange} defaultValue={selectedTimeRecord?.OwnerId}/>
      {displayFields}
    </div>
  );
};

TimeConfigFields.propTypes = {
  configFields: PropTypes.array,
  handleChange: PropTypes.func,
  updateRequiredFields: PropTypes.func,
};
